import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Eye, EyeOff } from "lucide-react";

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate("/dashboard");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const result = await register(name, email, password, phone);
    setLoading(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F8F6] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#E68A00] rounded-md flex items-center justify-center mx-auto mb-4">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <h1
            className="text-2xl font-bold text-[#1A2F24]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Create Account
          </h1>
          <p className="text-sm text-[#595959] mt-1">Sign up to start renting equipment</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E0D8] rounded-lg p-6 space-y-4">
          {error && (
            <div data-testid="register-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          <div>
            <Label htmlFor="name" className="text-[#1C1C1C]">Full Name</Label>
            <Input
              id="name"
              data-testid="register-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
              className="mt-1.5 bg-white border-[#E5E0D8] focus:ring-[#E68A00]"
            />
          </div>
          <div>
            <Label htmlFor="reg-email" className="text-[#1C1C1C]">Email</Label>
            <Input
              id="reg-email"
              data-testid="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="mt-1.5 bg-white border-[#E5E0D8] focus:ring-[#E68A00]"
            />
          </div>
          <div>
            <Label htmlFor="phone" className="text-[#1C1C1C]">Phone (optional)</Label>
            <Input
              id="phone"
              data-testid="register-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="mt-1.5 bg-white border-[#E5E0D8] focus:ring-[#E68A00]"
            />
          </div>
          <div>
            <Label htmlFor="reg-password" className="text-[#1C1C1C]">Password</Label>
            <div className="relative mt-1.5">
              <Input
                id="reg-password"
                data-testid="register-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                className="bg-white border-[#E5E0D8] focus:ring-[#E68A00] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#595959]"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            data-testid="register-submit"
            disabled={loading}
            className="w-full bg-[#E68A00] hover:bg-[#CC7A00] disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
          <p className="text-center text-sm text-[#595959]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#E68A00] font-medium hover:underline" data-testid="register-login-link">
              Log In
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
